import { Link } from 'react-router';
import { ArrowRight } from 'lucide-react';
import { PageWrapper } from '@/layout/components/PageWrapper.tsx';
import { PageHeader } from '@/layout/components/PageHeader.tsx';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card.tsx';
import { Button } from '@/components/ui/button.tsx';
import { Badge } from '@/components/ui/badge.tsx';
import newsStyleExample from '@/assets/audio/tts-docs-examples/news-style.wav';
import pauseTagsExample from '@/assets/audio/tts-docs-examples/pause-tags.wav';
import whisperTagExample from '@/assets/audio/tts-docs-examples/whisper-tag.wav';

interface TagRow {
  tag: string;
  label: string;
  description: string;
}

const NON_SPEECH_TAGS: TagRow[] = [
  { tag: '[sigh]', label: 'אנחה', description: 'הוספת קול אנחה. האופי הרגשי מושפע מה-Style Prompt שנלווה אליו.' },
  { tag: '[laughing]', label: 'צחוק', description: 'הוספת קול צחוק. לתוצאה טובה יש לצרף הנחיה מפורשת, למשל "הגיבו בצחוק משועשע".' },
  { tag: '[uhm]', label: 'היסוס', description: 'הוספת קול היסוס קל, לתחושה טבעית ושיחתית יותר.' },
];

const STYLE_MODIFIER_TAGS: TagRow[] = [
  { tag: '[sarcasm]', label: 'טון סרקסטי', description: 'מקנה טון סרקסטי למשפט שאחרי התגית.' },
  { tag: '[robotic]', label: 'סגנון רובוטי', description: 'הופך את הדיבור שאחרי התגית לרובוטי. מומלץ לשלב גם ב-Style Prompt.' },
  { tag: '[shouting]', label: 'צעקה', description: 'מגביר את עוצמת הדיבור שאחרי התגית.' },
  { tag: '[whispering]', label: 'לחישה', description: 'מנמיך את עוצמת הדיבור שאחרי התגית ללחישה.' },
  { tag: '[extremely fast]', label: 'קצב מהיר מאוד', description: 'מגביר את מהירות הדיבור שאחרי התגית - מתאים לאזהרות משפטיות או דיבור קצבי.' },
];

const PACING_TAGS: TagRow[] = [
  { tag: '[short pause]', label: 'הפסקה קצרה', description: 'הפסקה קצרה (כ-250 מילישניות), דומה לפסיק.' },
  { tag: '[medium pause]', label: 'הפסקה בינונית', description: 'הפסקה בינונית (כ-500 מילישניות), דומה לסוף משפט.' },
  { tag: '[long pause]', label: 'הפסקה ארוכה', description: 'הפסקה משמעותית (כשנייה ומעלה) לצורך אפקט דרמטי. מומלץ שלא להגזים בשימוש.' },
];

const VOCALIZED_TAGS: TagRow[] = [
  { tag: '[scared]', label: 'מפוחד', description: 'המילה "scared" נהגית בפועל באנגלית בתוך המשפט, והטון הופך למפוחד.' },
  { tag: '[curious]', label: 'סקרן', description: 'המילה "curious" נהגית בפועל, והטון הופך לסקרן.' },
  { tag: '[bored]', label: 'משועמם', description: 'המילה "bored" נהגית בפועל, והטון הופך למונוטוני ומשועמם.' },
];

function TagTable({ rows }: { rows: TagRow[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="border-b text-right text-muted-foreground">
            <th className="py-2 pl-2 font-medium">תגית</th>
            <th className="py-2 pl-2 font-medium">שם</th>
            <th className="py-2 font-medium">הסבר</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.tag} className="border-b last:border-0 align-top">
              <td className="py-2 pl-2 whitespace-nowrap">
                <code className="bg-slate-100 text-slate-800 px-1.5 py-0.5 rounded text-xs font-mono">{row.tag}</code>
              </td>
              <td className="py-2 pl-2 whitespace-nowrap">{row.label}</td>
              <td className="py-2 text-muted-foreground">{row.description}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CodeBlock({ children }: { children: string }) {
  return (
    <pre className="bg-slate-100 text-slate-800 rounded-md p-3 text-xs overflow-x-auto whitespace-pre-wrap font-mono text-left" dir="ltr">
      {children}
    </pre>
  );
}

export default function TextToSpeechDocsPage() {
  return (
    <PageWrapper>
      <PageHeader title="מדריך מורחב: יצירת קול מטקסט (Gemini TTS)" />

      <div className="max-w-3xl w-full space-y-5">
        <Button type="button" variant="ghost" size="sm" asChild className="w-fit">
          <Link to="/tools/text-to-speech">
            <ArrowRight className="size-4 ml-1" />
            חזרה לדף יצירת קול מטקסט
          </Link>
        </Button>

        <Card>
          <CardHeader>
            <CardTitle>שלושת מנופי השליטה בדיבור</CardTitle>
            <CardDescription>
              על מנת לקבל תוצאה צפויה ואיכותית, מומלץ להתאים בין שלושת המרכיבים הבאים כך שיובילו לאותה כוונה:
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4 text-sm">
            <div>
              <Badge variant="secondary" className="mb-1">1. הנחיית סגנון (Style Prompt)</Badge>
              <p className="text-muted-foreground">
                המרכיב המרכזי הקובע את הטון הרגשי הכללי ואופן ההגשה של כל קטע הדיבור. בדף יצירת הקול זהו שדה
                "אווירת הטקסט" (או הנחיה מותאמת אישית). לדוגמה: <code className="bg-slate-100 text-slate-800 px-1 rounded text-xs">קרא את הטקסט הבא בטון חם ומעודד</code>.
              </p>
            </div>
            <div>
              <Badge variant="secondary" className="mb-1">2. תוכן הטקסט עצמו</Badge>
              <p className="text-muted-foreground">
                המשמעות הסמנטית של המילים. טקסט שמתאים מבחינה תוכנית לסגנון המבוקש (למשל משפט המתאר שמחה, עבור
                טון שמח) יניב תוצאה טבעית ואמינה הרבה יותר מטקסט ניטרלי שאינו קשור לסגנון.
              </p>
            </div>
            <div>
              <Badge variant="secondary" className="mb-1">3. תגיות הבעה (Markup Tags)</Badge>
              <p className="text-muted-foreground">
                תגיות בסוגריים מרובעים כמו <code className="bg-slate-100 text-slate-800 px-1 rounded text-xs">[short pause]</code> משמשות
                להוספת אפקט נקודתי וממוקד (השהיה, שינוי קצב, לחישה וכו') בתוך קטע הדיבור, בנוסף להנחיית הסגנון
                הכללית - ולא במקומה.
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>תגיות הבעה נתמכות (Audio Tags)</CardTitle>
            <CardDescription>
              התגיות מוזנות ישירות בתוך תיבת הטקסט להמרה. בדף יצירת הקול ניתן ללחוץ על כפתורי התגיות מתחת לתיבת
              הטקסט כדי להוסיף אותן אוטומטית.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <h3 className="text-sm font-semibold">צלילים לא-מילוליים</h3>
              <TagTable rows={NON_SPEECH_TAGS} />
            </div>
            <div className="space-y-2">
              <h3 className="text-sm font-semibold">שינויי סגנון (חלים על הטקסט שאחרי התגית)</h3>
              <TagTable rows={STYLE_MODIFIER_TAGS} />
            </div>
            <div className="space-y-2">
              <h3 className="text-sm font-semibold">קצב והפסקות</h3>
              <TagTable rows={PACING_TAGS} />
            </div>
            <div className="space-y-2">
              <h3 className="text-sm font-semibold">
                תגיות "מדוברות" - לא מומלץ לשימוש כללי
              </h3>
              <p className="text-xs text-muted-foreground">
                בקטגוריה הבאה, המילה שבתוך התגית נהגית בפועל (באנגלית) בתוך המשפט, ולא רק משנה את הטון. לרוב זו
                תוצאה לא רצויה - מומלץ להשתמש בהנחיית סגנון (Style Prompt) במקום.
              </p>
              <TagTable rows={VOCALIZED_TAGS} />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>דוגמאות שמע אמיתיות</CardTitle>
            <CardDescription>
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <div className="text-sm font-medium">טון קריין חדשות (ללא תגיות)</div>
              <CodeBlock>
                {'Style Prompt: קרא את הכותרת הבאה בטון קריין חדשות רגוע ומקצועי\nText: משרדי חברת ידע פון ממשיכים לפעול כסדרם, ומספקים מענה מקצועי ואדיב לכל פונה.'}
              </CodeBlock>
              <audio controls src={newsStyleExample} className="w-full" />
            </div>

            <div className="space-y-2">
              <div className="text-sm font-medium">שימוש בתגיות הפסקה - [short pause] / [medium pause]</div>
              <CodeBlock>
                {'Style Prompt: קרא בבירור ובקצב נינוח\nText: נא להמתין בסבלנות. [short pause] הבקשה שלך מטופלת כעת. [medium pause] תודה על הסבלנות.'}
              </CodeBlock>
              <audio controls src={pauseTagsExample} className="w-full" />
            </div>

            <div className="space-y-2">
              <div className="text-sm font-medium">שימוש בתגית לחישה - [whispering]</div>
              <CodeBlock>
                {'Style Prompt: אמור זאת בעדינות ובנימה חמה\nText: [whispering] תזכורת עדינה: ניתן ליצור עמנו קשר בכל עת.'}
              </CodeBlock>
              <audio controls src={whisperTagExample} className="w-full" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>טיפים לניסוח טקסט ותגיות</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="list-disc pr-5 space-y-2 text-sm text-muted-foreground">
              <li>
                יש להקפיד שהנחיית הסגנון, תוכן הטקסט והתגיות כולם "מכוונים" לאותה כוונה - שילוב לא עקבי ביניהם
                עלול להוביל לתוצאה מבולבלת.
              </li>
              <li>
                עדיף טקסט עשיר ותיאורי על פני טקסט ניטרלי, בפרט כשרוצים להעביר רגש עדין (כגון דאגה, שמחה או
                הפתעה).
              </li>
              <li>
                מומלץ לבדוק כל תגית חדשה בנפרד לפני הפעלה מלאה - התנהגות של תגית לא מוכרת אינה תמיד צפויה מראש.
              </li>
              <li>
                יש להימנע משימוש מוגזם בתגיות הפסקה ארוכות או בתגיות סגנון קיצוניות, כדי לשמור על דיבור טבעי
                וברור.
              </li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </PageWrapper>
  );
}
